import { Component, EventEmitter, HostListener, Input, Output, ViewChild, OnChanges, SimpleChanges, OnInit, ElementRef } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AdvanceSearchFilter } from '../../models/advance-search/advance-search-filter.model';
import { AdvanceSearchLabels } from '../../models/advance-search/advance-search-label.model';
import { CombineProperties } from '../../models/advance-search/combine-properties.model';
import { IncludeProperties } from '../../models/advance-search/include-properties.model';
import { SearchTextHighlight } from '../../models/advance-search/search-text-highlight.model';
import { AnalysisToolTip } from '../../models/analysis-tool-tip.model';
import { AnalysisTreeModel } from '../../models/analysis-tree.model';
import { Analysis } from '../../models/analysis.model';
import { QuotedItem } from '../../models/quotable-items/quoted-item.model';
import AnalysisHelper from './dop-analysis.helper';
import { TreeNode } from 'primeng/api';
import { ProductMatrix } from '../../models/product-matrix.model';
import ProductMatrixHelper from './dop-product-matrix.helper';
import { OverlayPanel } from 'primeng/overlaypanel';
import { TTCheckbox, TreeTable } from 'primeng/treetable';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import { InputNumber } from 'primeng/inputnumber';
import { TranslationService } from 'projects/dop-core/src/public-api';

@Component({
    selector: 'dop-analysis',
    templateUrl: './dop-analysis.component.html',
    styleUrls: ['./dop-analysis.component.scss']
})
export class DopAnalysisComponent implements OnInit, OnChanges {
    isAnalysisExists: boolean = true;
    @ViewChild('overlayReference') overlayReference: OverlayPanel;
    @ViewChild('advanceSearch') advanceSearchReference: OverlayPanel;
    @ViewChild('matrixTree') matrixTreeReference: OverlayPanel;
    @ViewChild('tt') dopTreeTable: TreeTable;
    @ViewChild('ipt') ipt: ElementRef;
    @ViewChild('checkbox') checkbox : HTMLInputElement;
  
    // Get the Analysis details of the quotation to be displayed
    get analysis(): QuotedItem {
        return this.inputAnalysis;
    }
    @Input()
    set analysis(val: QuotedItem) {
        if (val) {
            this.inputAnalysis = { ...val };
            this.commercialItems = AnalysisHelper.mapToViewModel(this.inputAnalysis.quotationCommercialItem,
                this.displayPrice, this.selectedAnalysis, this.alreadySelectedAnalysis);
            if (this.commercialItems.length > 0) {
                this.isAnalysisExists = true;
            }
            else {
                this.isAnalysisExists = false;
            }
            this.dopTreeTable?.reset();
            if (!this.advanceSearchEnabled) {
                this.advanceSearchInputRefine();
            }
        }

      
        this.initializeSampleInitForm(this.commercialItems);

        //Only if MultipleDetrmination is enabled patch the value
        if(this.enableMultiplDetermination){
        this.alreadySelectedAnalysis?.forEach(analysis =>{
            this.patchValueByEurodatKey(analysis.eurodatKey, analysis.executionNumber);
        });
       }

       //reset the tab only whne Matrix is selected
        if(this.previousSelectedMatrix !== undefined && this.selectedMatrix !== this.previousSelectedMatrix && this.UseProductMatrix) 
          this.resetSampleInitForm(this.commercialItems);

    }

    initializeSampleInitForm(items: any[]) {
        items?.forEach((item) => {
          const controlId = item.data.eurodatKey;
          this.sampleInitForm.addControl(controlId, this.createFormControl());
      
          if (item.children) {
            this.initializeSampleInitForm(item.children);
          }
        });
      }

      patchValueByEurodatKey(eurodatKey: string, value: any) {
        if (this.sampleInitForm.contains(eurodatKey)) {
          this.sampleInitForm.get(eurodatKey).patchValue(value);
        }
      }

      resetSampleInitForm(items: any[])
      {
        items?.forEach((item) => {
            const controlId = item.data.eurodatKey;
            this.sampleInitForm.get(controlId).patchValue(1);
        
            if (item.children) {
              this.resetSampleInitForm(item.children);
            }
          });
      }
      
      createFormControl() {
        return this.fb.control(1, [
          Validators.maxLength(3), RxwebValidators.range({ minimumNumber: 1, maximumNumber: this.maxSelectionOfAnalyses })
        ]);
      }

    @Input()
    set productMatrices(products: ProductMatrix[]) {
        if (products?.length === 1 && products[0].descendants?.length === 0) {
            this.selectedMatrix = { label: products[0]?.productName, data: products[0].productCode };
            this.previousSelectedMatrix = this.selectedMatrix;
            this.matrixChangeEvent.emit({ productCode: this.selectedMatrix.data, productName: this.selectedMatrix.label });
        }
        this.matrices = ProductMatrixHelper.mapProductMatrixToTree(products);
    }

    @Input()
    set selectedProductMatrix(product: ProductMatrix) {
        if (product) {
            this.selectedMatrix = { label: product.productName, data: product.productCode };            
            this.previousSelectedMatrix = this.selectedMatrix;
        }
    }

    // Set the property to show or hide the product matrix
    @Input() UseProductMatrix: boolean;
    // Set the property to show or hide the price
    @Input() displayPrice: boolean;

    // Set this property to display or hide tooltip
    @Input() displayToolTip: boolean;

    // Set the property to show or hide the favorite icon next to ci
    @Input() displayFavorite: boolean;
    
    // Set the property to show or hide only favorite cis
    @Input() showFavoriteItems: boolean;

    // Set ToolTipData to be shown
    @Input() toolTipData: AnalysisToolTip;

    // Set the already selected Analysis to display in UI as selected
    @Input() alreadySelectedAnalysis: Analysis[];

    // Set the analysis which needs to be manually deselected from the UI
    @Input() unselectAnalysis: Analysis[];

    // All Labels Needed for advance Search Component
    @Input() advanceSearchLabels: AdvanceSearchLabels = new AdvanceSearchLabels();

    // Property to show advance search or not
    @Input() showAdvanceSearch = false;

    //Set EnableMulipelDeternination
    @Input() enableMultiplDetermination : boolean;

    //set MaxSelectionOfAnalyses
    @Input() maxSelectionOfAnalyses : number;

    // Event triggered when the an analysis is selected
    @Output() analysisSelectedEvent = new EventEmitter<Analysis[]>();

    // Event triggered when the an analysis is unSelected
    @Output() analysisUnSelectedEvent = new EventEmitter<Analysis[]>();

    // Event triggered when the an analysis is unSelected
    @Output() toolTipEvent = new EventEmitter<Analysis>();

    // Event triggered when the an analysis marked as favorite
    @Output() favoriteAnalysisEvent = new EventEmitter<Analysis>();

    // Event triggered when the download icon is clicked to download technical document
    @Output() downloadTechnicalSalesDocumentEvent = new EventEmitter<any>();

    // Event triggered when the an analysis marked as un-favorite
    @Output() unFavoriteAnalysisEvent = new EventEmitter<Analysis[]>();

    // Event triggered when advance search is requested, with filter object
    @Output() advanceSearchFilterEvent = new EventEmitter<AdvanceSearchFilter>();

    // Event triggered when advance search checkbox is changed
    @Output() advanceSearchCheckBoxChangeEvent = new EventEmitter<boolean>();

    // Event triggered when matrix is selected/changed
    @Output() matrixChangeEvent = new EventEmitter<ProductMatrix>();

    @Output() IsExecutionNumberValid = new EventEmitter<boolean>();

    inputAnalysis: QuotedItem;
    commercialItems: AnalysisTreeModel[];
    cols: any[];
    selectedAnalysis: AnalysisTreeModel[] = [];
    maxInputErrorMessage: string;
    currentNode: any;
    advanceSearchEnabled = false;
    advanceSearchIncludeProperties: IncludeProperties;
    advanceSearchCombineProperties: CombineProperties;
    searchText = '';
    searchTextUpdate = new Subject<string>();
    highlightText: SearchTextHighlight;
    selectedMatrix: TreeNode;
    matrices: TreeNode[];
    previousSelectedMatrix: TreeNode;
    sampleCount: number;
    sampleInitForm : FormGroup;
    excutionNumber: number;
    checked: boolean;
    checkboxclick: string;
    
    constructor(private fb: FormBuilder
    ) { 
        this.sampleInitForm = this.fb.group({});
    }

    ngOnInit(): void {
        this.highlightText = new SearchTextHighlight();
        this.resetAdvanceSearchProperties();
        this.searchTextUpdate.pipe(
            debounceTime(400),
            distinctUntilChanged())
            .subscribe(value => this.advanceSearchInputRefine());
        this.maxInputErrorMessage = 'Max\t' + this.maxSelectionOfAnalyses;    

    }

    onMatrixNodeSelect(event: any) {
        this.selectedAnalysis.length = 0;
        this.selectedMatrix = event.node;
        this.matrixTreeReference.hide();
        if (!this.previousSelectedMatrix || this.previousSelectedMatrix?.data !== this.selectedMatrix?.data) {
            this.matrixChangeEvent.emit({ productCode: this.selectedMatrix.data, productName: this.selectedMatrix.label });
            this.previousSelectedMatrix = this.selectedMatrix;
        }
    }

    get matrixPlaceHolder() {
        return (this.selectedMatrix && this.selectedMatrix?.label) ? this.selectedMatrix.label : TranslationService.translate('SELECT_MATRIX');
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.unselectAnalysis) {
            if (this.unselectAnalysis && this.unselectAnalysis.length > 0) {
                this.unCheckAnalysis();
            }
        }
    }

    unCheckAnalysis() {
        this.unselectAnalysis.forEach(analysis => {
            const unselectedNode = AnalysisHelper.unselectItemFromCommercialItems(this.commercialItems, analysis);
            this.onCheckboxClicked({ node: unselectedNode }, false);
            this.selectedAnalysis = AnalysisHelper.unselectItemFromSelectedAnalysis(this.selectedAnalysis, analysis);
        });
        this.selectedAnalysis = [...this.selectedAnalysis];
        this.commercialItems = [...this.commercialItems];
    }

    onCheckboxClicked(selectedNode: any, isSelected = false) {
        const listAnalysis = AnalysisHelper.getSelectedAnalysis(selectedNode, this.inputAnalysis, isSelected);
        this.checked = isSelected;
        if(!isSelected)
        {
            listAnalysis?.forEach(item =>{
                this.sampleInitForm.get(item.eurodatKey).patchValue(1);
                this.sampleInitForm.get(item.eurodatKey).setErrors(null);
            });
        }

        isSelected ? this.emitSelectedAnalysis(listAnalysis) : this.emitUnSelectedAnalysis(listAnalysis);
    }

    onFavorite(selectedNode: any) {
        if (!selectedNode.node.data.isFavorite) {
            const analysis = AnalysisHelper.getFavoriteAnalysis(selectedNode);
            this.favoriteAnalysisEvent.emit(analysis);
        } else {
            const duplicateNode = this.getDuplicateFavoriteNode(selectedNode);
            if (duplicateNode) {
                // just making it unfavorite without updating the database
                duplicateNode.data.isFavorite = false;
                return;
            }

            const unFavAnalysis = AnalysisHelper.getUnFavoriteAnalysis(selectedNode, this.inputAnalysis);
            this.unFavoriteAnalysisEvent.emit(unFavAnalysis);
        }
    }

    onDownloadTechnicalSalesDocumentClick(selectedNode: any) {
        this.downloadTechnicalSalesDocumentEvent.emit(selectedNode);
    }

    onValueChange(event: number, name: string, selectedNode: any): void {
        
        const newValue = Math.max(0, Math.min(event, this.maxSelectionOfAnalyses));
      
        this.sampleInitForm.get(name).setValue(event);
      
        if (event == 0 ||newValue !== event) {
          this.sampleInitForm.get(name).setErrors({ range: true });
        } else {
          this.sampleInitForm.get(name).setErrors(null);
        }
      
        selectedNode.node.data.executionNumber = event;
      }

      onSampleCountUpdate(event: number, name: string, selectedNode: any): void {
        this.onValueChange(event, name, selectedNode);
      
        const checkboxClick = this.checkboxEventHTML(selectedNode);
      
        if (checkboxClick === 'true') {
          this.onCheckboxClicked(selectedNode, true);
        }
      }
      

    private checkboxEventHTML(selectedNode: any) {
        const clicked = document.getElementById(selectedNode.node.data.eurodatKey);
        const checkboxDiv = clicked.querySelector('div[role="checkbox"]');
        const checkboxClick = checkboxDiv.getAttribute('aria-checked');
        return checkboxClick;
    }

      oninput(event: Event, name: string, selectedNode: any): void {
          const inputElement = event.target as HTMLInputElement;

          if (inputElement.value == '' || inputElement.value == '0'){
              this.sampleInitForm.get(name).patchValue(inputElement.value);
              this.sampleInitForm.get(name).setErrors({ range: true });
          }

          else {
              const inputNumber = parseInt(inputElement.value.replace(/\D/g, ''), 10);
              if (inputNumber)
                  this.onValueChange(inputNumber, name, selectedNode);
              else
                  this.sampleInitForm.get(name).patchValue(1);
          }
          const checkboxClick = this.checkboxEventHTML(selectedNode);

          if (checkboxClick === 'true') {
              this.onCheckboxClicked(selectedNode, true);
          }
    }

    emitSelectedAnalysis(analysis: Analysis[]) {
        console.log(analysis);
        console.log(this.sampleInitForm.valid)
        this.IsExecutionNumberValid.emit(!this.sampleInitForm.valid);
        this.analysisSelectedEvent.emit(analysis);
    }

    emitUnSelectedAnalysis(analysis: Analysis[]) {
        this.IsExecutionNumberValid.emit(!this.sampleInitForm.valid);
        this.analysisUnSelectedEvent.emit(analysis);
    }

    openToolTip(selectedNode: any, event: Event, overlayReference: OverlayPanel) {
        if (this.currentNode && selectedNode) {
            if (selectedNode.node && selectedNode.node.data && this.currentNode !== selectedNode.node.data.code) {
                this.currentNode = selectedNode.node.data.code;
                overlayReference.hide();
                setTimeout(() => {
                    this.getAnalysisData(selectedNode);
                    overlayReference.show(event);
                }, 500);
            } else {
                overlayReference.show(event);
            }
        } else {
            this.currentNode = selectedNode.node.data.code;
            this.getAnalysisData(selectedNode);
            overlayReference.show(event);
        }
    }

    getAnalysisData(selectedNode) {
        const analysis = AnalysisHelper.getAnalysisFromNode(selectedNode);
        this.toolTipData = null;
        this.toolTipEvent.emit(analysis);
    }

    @HostListener('window:scroll', ['$event'])
    onScrollEvent = () => {
        if (this.overlayReference) {
            this.overlayReference.hide();
            this.advanceSearchReference.hide();
            this.matrixTreeReference.hide();
            this.toolTipData = null;
            this.currentNode = null;
        }
    }

    //#region Advance Search

    closeAdvanceSearch() {
        this.advanceSearchReference.hide();
    }

    advanceSearchInputRefine() {
        this.searchText = this.searchText.trim();
        this.setSearchText();
        if (this.searchText.length > 2 || this.searchText === '') {
            this.overlayReference.hide();
            if (!this.advanceSearchEnabled) {
                // Internal Search if advance search is disabled
                this.dopTreeTable?.filterGlobal(this.searchText, 'contains');
                setTimeout(() => {
                    this.updateNodeExpansion();
                }, 0);
            } else if (JSON.stringify(this.advanceSearchCombineProperties) === JSON.stringify(new CombineProperties())
                && JSON.stringify(this.advanceSearchIncludeProperties) === JSON.stringify(new IncludeProperties())) {
                // Perform Client Search if no Properties in include/combine are selected
                this.dopTreeTable?.filterGlobal(this.searchText, 'contains');
                setTimeout(() => {
                    this.updateNodeExpansion();
                }, 0);
            } else {
                this.refineSearchEvent();
            }
        }
    }

    updateNodeExpansion(){
        if(this.searchText == ''){
            this.dopTreeTable?._value?.forEach(x => x.expanded = false);
        }
        else{
            if(!this.dopTreeTable?.filteredNodes){
                this.dopTreeTable?._value?.forEach(x => x.expanded = true);
            }
            else{
                this.dopTreeTable?._value?.forEach(x => this.dopTreeTable?.filteredNodes?.some(filteredNode => filteredNode.data.Code === x.data.Code) ? x.expanded = true : x.expanded = false);
            }
        }
    }

    resetSearchText() {
        this.searchText = '';
        this.advanceSearchInputRefine();
    }

    refineSearchEvent() {
        const advanceSearch = new AdvanceSearchFilter();
        advanceSearch.searchText = this.searchText;
        advanceSearch.includeProperties = this.advanceSearchIncludeProperties;
        advanceSearch.combineProperties = this.advanceSearchCombineProperties;
        this.setSearchText();
        if (this.advanceSearchEnabled) {
            this.advanceSearchFilterEvent.emit(advanceSearch);
        }
        this.closeAdvanceSearch();
    }

    clearAllFiltersEvent() {
        this.resetAdvanceSearchProperties();
        this.setSearchText();
        this.advanceSearchCheckBoxChangeEvent.emit(false);
    }

    advanceSearchCheckBoxChange() {
        this.setSearchText();
        this.advanceSearchCheckBoxChangeEvent.emit(this.advanceSearchEnabled);
    }

    resetAdvanceSearchProperties() {
        this.advanceSearchIncludeProperties = new IncludeProperties();
        this.advanceSearchCombineProperties = new CombineProperties();
    }

    setSearchText() {
        this.highlightText = new SearchTextHighlight();
        this.highlightText.globalSearch = this.searchText;

        if (this.advanceSearchEnabled) {
            this.highlightText.CASNumber = this.advanceSearchCombineProperties.CASNumber;
            this.highlightText.applicableOn = this.advanceSearchCombineProperties.applicableOn;
            this.highlightText.comments = this.advanceSearchCombineProperties.comments;
            this.highlightText.description = this.advanceSearchCombineProperties.description;
            this.highlightText.method = this.advanceSearchCombineProperties.method;
            this.highlightText.objective = this.advanceSearchCombineProperties.objective;
            this.highlightText.parameterCode = this.advanceSearchCombineProperties.parameterCode;
            this.highlightText.parameterName = this.advanceSearchCombineProperties.parameterName;
        }
    }

    includePropertyChanged() {
        this.advanceSearchEnabled = Object.values(this.advanceSearchIncludeProperties).some(x => x);
        this.advanceSearchCheckBoxChange();
    }
 
    private getDuplicateFavoriteNode(selectedNode: any): AnalysisTreeModel {
        const selectedNodeSubTree = selectedNode.parent ? this.commercialItems.filter(x => x.data?.code == selectedNode.parent?.data?.code) : this.commercialItems;
        const favAnalysesWithSameCode = selectedNodeSubTree.filter(x => x.data?.code == selectedNode?.node?.data?.code && x.data?.isFavorite);
        if (favAnalysesWithSameCode.length > 1) {
            // multiple favorite nodes found with same code under same parent
            return favAnalysesWithSameCode.filter(x => x.data.eurodatKey == selectedNode.node.data.eurodatKey).shift();
        }
    }
    //#endregion Advance Search
}
